// pages/tenancy-courses/TenancyCourseAdd.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Grid, Button, Typography, Alert, Chip, Snackbar, Modal, Paper, IconButton, Box } from '@mui/material';
import KeyboardArrowLeftTwoToneIcon from '@mui/icons-material/KeyboardArrowLeftTwoTone';
import CloseIcon from '@mui/icons-material/Close';
import MainCard from '../../components/cards/MainCard';
import TenancyCourseForm from './TenancyCourseForm';
import CourseProgressIndicator from '../../components/Loading/CourseProgressIndicator';
import { useCourseCreationFlow } from './useCourseCreationFlow';

export default function TenancyCourseAdd() {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem("user"));
    const isAdmin = user.role === "Administrators";
    const { tenancyId } = useSelector((state) => state.tenancy);
    const {
        state,
        error,
        createdCourseId,
        executeCreationFlow,
        reset,
        isProcessing,
        isComplete,
        isError,
    } = useCourseCreationFlow('create');

    const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(true);
    const [formData, setFormData] = useState({
        courseName: '',
        languageCodes: [],
        published: isAdmin ? false : true,
        sharedTenancyIds: isAdmin ? [] : [tenancyId],
        publishToFamily: false,
        isLms: true,
    });

    const [pendingFiles, setPendingFiles] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Auto-navigate after successful creation
    useEffect(() => {
        if (isComplete && createdCourseId) {
            setSnackbar({
                open: true,
                message: 'Course created successfully! Redirecting to course editor...',
                severity: 'success',
            });

            // Navigate to edit page after 2 seconds
            const timer = setTimeout(() => {
                navigate(`/tenancy-courses/edit/${createdCourseId}`);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [isComplete, createdCourseId, navigate]);

    const handleSubmit = async () => {
        // Validate that at least content is uploaded
        if (!pendingFiles.content) {
            setSnackbar({
                open: true,
                message: 'Please upload course content (ZIP file)',
                severity: 'warning',
            });
            return;
        }

        await executeCreationFlow(formData, pendingFiles);
    };

    const handleRetry = () => {
        reset();
    };

    const handleBack = () => {
        if (isProcessing) {
            const confirmLeave = window.confirm(
                'Course creation is in progress. Are you sure you want to leave? This may cause the upload to fail.'
            );
            if (!confirmLeave) return;
        }
        navigate(-1);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleDisclaimerAccept = () => {
        setDisclaimerModalOpen(false);
    };

    const handleDisclaimerCancel = () => {
        navigate('/tenancy-courses');
    };

    const handleDisclaimerClose = () => {
        navigate('/tenancy-courses');
    };

    return (
        <>
            <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <Button
                        onClick={handleBack}
                        variant="contained"
                        startIcon={<KeyboardArrowLeftTwoToneIcon />}
                        disabled={isProcessing}
                    >
                        Back
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <Typography variant="h2">
                            Create New Course
                        </Typography>

                        <Chip
                            color={'primary'}
                            label={'BETA'}
                            sx={{
                                height: 25,
                                fontSize: '0.825rem',
                                fontWeight: 'bold',
                                px: 1,
                                '& .MuiChip-label': {
                                    px: 0.15
                                }
                            }} 
                        />
                    </Box>

                    {!isProcessing && !isComplete && !isError && (
                        <TenancyCourseForm
                            formData={formData}
                            onChange={setFormData}
                            pendingFiles={pendingFiles}
                            onFileChange={setPendingFiles}
                            onSubmit={handleSubmit}
                            isProcessing={isProcessing}
                            mode="create"
                        />
                    )}

                    {(isProcessing || isComplete || isError) && (
                        <CourseProgressIndicator
                            state={state}
                            error={error}
                            onRetry={handleRetry}
                            onComplete={() => navigate(`/tenancy-courses/edit/${createdCourseId}`)}
                            mode="create"
                        />
                    )}
                </Grid>
            </Grid>

            {/* Content Responsibility Disclaimer Modal */}
            <Box sx={{ position: 'relative' }}>
                {disclaimerModalOpen && (
                    <IconButton
                        onClick={handleDisclaimerClose}
                        sx={{
                            position: 'fixed',
                            right: 'calc(50% - 300px)',
                            top: 'calc(50% - 10% - 220px)',
                            bgcolor: 'background.default',
                            boxShadow: 2,
                            '&:hover': {
                                bgcolor: 'background.default',
                                opacity: 0.9
                            },
                            width: 40,
                            height: 40,
                            zIndex: 1301
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
                <Modal
                    open={disclaimerModalOpen}
                    disableEscapeKeyDown
                    aria-labelledby="content-disclaimer-modal"
                >
                    <Paper sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '8px',
                        width: 600,
                        p: 4,
                        outline: 'none',
                        backgroundColor: 'background.default',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            Content Responsibility Agreement
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 3, mt: 2 }}>
                            By uploading your own course content, you acknowledge and agree that:
                        </Typography>

                        <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                                You are solely responsible for all content you upload to this platform
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                                You own or have the necessary rights, licenses, and permissions to use and upload this content
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                                Your content does not infringe upon any third-party intellectual property rights
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                                Your content complies with all applicable laws and regulations
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                                Boxphish bears no responsibility for any content you upload and you will hold harmless Boxphish from any claims, damages, or legal action arising from you uploaded content
                            </Typography>
                        </Box>

                        <Alert severity="info" sx={{ mb: 3 }}>
                            Please ensure your course content meets all quality and legal standards before uploading.
                        </Alert>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={handleDisclaimerCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleDisclaimerAccept}
                            >
                                Accept
                            </Button>
                        </Box>
                    </Paper>
                </Modal>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}